import { Accordion, Badge, Input, Label, lucideIcons, Switch } from '@round/creatorbase-ui';
import { FilterModeToggle } from './FilterModeToggle';
import pick from 'lodash/pick';
import cn from 'classnames';
import styles from './FilterSidebar.module.css';
import { platformOptions } from '../../utils/creatorSearchUtils';
import { Filters, PlatformsFilter, getActiveFilterCount, minMax } from '../../utils/filters';
import { creatorbase, DEFAULT_OPERATOR } from '@round/api';
import { toNumberOrUndefined } from '@round/utils';
import { MinMaxInputs } from '@round/creatorbase-ui';
import {
    AudienceLocationFilter,
    AverageDraftTimeFilter,
    ContentTagsExternalFilter,
    ContentTagItem,
    LocationExternalFilter,
    LastUsedFilter,
    PostingFrequencyFilter,
    SpotifyTrackFilter,
    SpotifyGenreFilter,
    SpotifyArtistFilter,
} from '../filters';
import { TiktokAudioUrlFilter } from '../filters/TiktokAudioUrlFilter/TiktokAudioUrlFilter';

const { Tag, Users, BarChart3, DollarSign, Music, HeartHandshake } = lucideIcons;

type Props = {
    filters: Filters;
    onFiltersChange: (filters: Filters) => void;
    disabledFilters?: Partial<Record<keyof Pick<Filters, 'platforms'>, boolean>>;
};

export function FilterSidebar({ filters, onFiltersChange, disabledFilters }: Props) {
    const {
        platforms,
        contentTags,
        locations,
        audienceLocations,
        followerRange,
        avgViews,
        avgEngagementRate,
        estimatedCpe,
        responseRate,
        avgDraftTime,
        lastUsed,
        postingFrequency,
        costRange,
        hasCost,
        tiktokAudioIds,
        spotifyTrackIds,
        spotifyGenres,
        spotifyArtistIds,
    } = filters;

    const togglePlatform = (value: Extract<creatorbase.Platform, 'tiktok' | 'instagram'>) => {
        const current = platforms?.conditions ?? [];
        const next = current.includes(value) ? current.filter((p) => p !== value) : [...current, value];
        const nextPlatforms: PlatformsFilter | undefined = next.length
            ? { conditions: next, operator: platforms?.operator }
            : undefined;
        onFiltersChange({ ...filters, platforms: nextPlatforms });
    };

    const hasMultiplePlatforms = filters.platforms?.conditions.length && filters.platforms?.conditions.length >= 2;
    const hasMultipleContentTags = (contentTags?.conditions.length ?? 0) >= 2;

    const platformCount = getActiveFilterCount(pick(filters, ['platforms', 'contentTags']));
    const audienceCount = getActiveFilterCount(pick(filters, ['locations', 'audienceLocations', 'followerRange']));
    const performanceCount = getActiveFilterCount(pick(filters, ['avgViews', 'avgEngagementRate', 'estimatedCpe']));
    const collaborationCount = getActiveFilterCount(
        pick(filters, ['responseRate', 'avgDraftTime', 'lastUsed', 'postingFrequency'])
    );
    const costCount = getActiveFilterCount(pick(filters, ['costRange', 'hasCost']));
    const audioCount = getActiveFilterCount(
        pick(filters, ['tiktokAudioIds', 'spotifyTrackIds', 'spotifyGenres', 'spotifyArtistIds'])
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.headerTitle}>FILTERS</h2>
            </div>

            <Accordion type="multiple" className={styles.accordionWrap}>
                <Accordion.Item value="platform-and-tags">
                    <Accordion.Trigger className={styles.trigger}>
                        <span className={styles.triggerInner}>
                            <Tag className={styles.triggerIcon} />
                            <span>Platform & Tags</span>
                            {platformCount > 0 && <Badge size="sm">{platformCount}</Badge>}
                        </span>
                    </Accordion.Trigger>

                    <Accordion.Content className={styles.content}>
                        <div className={styles.filterItem}>
                            <div className={styles.filterLabelRow}>
                                <Label className={styles.filterLabel}>Platform</Label>
                                {hasMultiplePlatforms && (
                                    <FilterModeToggle
                                        mode={platforms?.operator}
                                        onModeChange={(m) =>
                                            onFiltersChange({
                                                ...filters,
                                                platforms: platforms ? { ...platforms, operator: m } : undefined,
                                            })
                                        }
                                    />
                                )}
                            </div>
                            <div className={styles.platformBadges}>
                                {platformOptions.map((opt) => {
                                    const value = opt.value;
                                    const selected = platforms?.conditions.includes(value);
                                    return (
                                        <Badge
                                            key={value}
                                            variant={selected ? 'default' : 'outline'}
                                            className={cn(styles.platformBadge, {
                                                [styles.badgeDisabled]: disabledFilters?.platforms,
                                            })}
                                            onClick={
                                                disabledFilters?.platforms ? undefined : () => togglePlatform(value)
                                            }
                                        >
                                            {opt.label}
                                        </Badge>
                                    );
                                })}
                            </div>
                            {hasMultiplePlatforms && (
                                <p className={styles.filterHelperText}>
                                    Showing creators on{' '}
                                    {(platforms?.operator ?? DEFAULT_OPERATOR) === 'OR' ? 'any' : 'all'} selected
                                    platform
                                </p>
                            )}
                        </div>

                        <div className={styles.filterItem}>
                            <div className={styles.filterLabelRow}>
                                <Label className={styles.filterLabel}>Content Tags</Label>
                                {hasMultipleContentTags && (
                                    <FilterModeToggle
                                        mode={contentTags?.operator ?? 'OR'}
                                        onModeChange={(operator) =>
                                            onFiltersChange({
                                                ...filters,
                                                contentTags: contentTags ? { ...contentTags, operator } : undefined,
                                            })
                                        }
                                    />
                                )}
                            </div>
                            <ContentTagsExternalFilter
                                value={contentTags?.conditions ?? []}
                                onChange={(value: ContentTagItem[]) => {
                                    onFiltersChange({
                                        ...filters,
                                        contentTags: value.length
                                            ? { conditions: value, operator: contentTags?.operator }
                                            : undefined,
                                    });
                                }}
                            />
                            {hasMultipleContentTags && (
                                <p className={styles.filterHelperText}>
                                    Showing creators matching {(contentTags?.operator ?? 'OR') === 'OR' ? 'any' : 'all'}{' '}
                                    selected tag
                                    {(contentTags?.operator ?? 'OR') === 'OR' ? '' : 's'}
                                </p>
                            )}
                        </div>
                    </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="audience">
                    <Accordion.Trigger className={styles.trigger}>
                        <span className={styles.triggerInner}>
                            <Users className={styles.triggerIcon} />
                            <span>Audience</span>
                            {audienceCount > 0 && <Badge size="sm">{audienceCount}</Badge>}
                        </span>
                    </Accordion.Trigger>

                    <Accordion.Content className={styles.content}>
                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Creator Location</Label>
                            <LocationExternalFilter
                                value={locations ?? []}
                                onChange={(value) => {
                                    onFiltersChange({ ...filters, locations: value });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <div className={styles.filterLabelRow}>
                                <Label className={styles.filterLabel}>Top Audience Location</Label>
                                {audienceLocations && audienceLocations.conditions.length >= 2 && (
                                    <FilterModeToggle
                                        mode={audienceLocations.operator}
                                        onModeChange={(operator) =>
                                            onFiltersChange({
                                                ...filters,
                                                audienceLocations: { ...audienceLocations, operator },
                                            })
                                        }
                                    />
                                )}
                            </div>
                            <AudienceLocationFilter
                                value={audienceLocations}
                                onChange={(value) => onFiltersChange({ ...filters, audienceLocations: value })}
                            />
                            {audienceLocations && audienceLocations.conditions.length >= 2 && (
                                <p className={styles.filterHelperText}>
                                    Showing creators matching{' '}
                                    {(audienceLocations.operator ?? DEFAULT_OPERATOR) === 'OR' ? 'any' : 'all'} selected
                                    location
                                    {(audienceLocations.operator ?? DEFAULT_OPERATOR) === 'OR' ? '' : 's'}
                                </p>
                            )}
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Follower Range</Label>
                            <MinMaxInputs
                                min={followerRange?.min}
                                max={followerRange?.max}
                                debounce={500}
                                onChange={(min, max) => {
                                    onFiltersChange({ ...filters, followerRange: minMax(min, max) });
                                }}
                            />
                        </div>
                    </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="performance">
                    <Accordion.Trigger className={styles.trigger}>
                        <span className={styles.triggerInner}>
                            <BarChart3 className={styles.triggerIcon} />
                            <span>Performance</span>
                            {performanceCount > 0 && <Badge size="sm">{performanceCount}</Badge>}
                        </span>
                    </Accordion.Trigger>
                    <Accordion.Content className={styles.content}>
                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Avg. Plays</Label>
                            <MinMaxInputs
                                min={avgViews?.min}
                                max={avgViews?.max}
                                debounce={500}
                                onChange={(min, max) => {
                                    onFiltersChange({ ...filters, avgViews: minMax(min, max) });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Avg. Engagement Rate (%)</Label>
                            <MinMaxInputs
                                min={avgEngagementRate?.min}
                                max={avgEngagementRate?.max}
                                debounce={500}
                                onChange={(min, max) => {
                                    onFiltersChange({ ...filters, avgEngagementRate: minMax(min, max) });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>CPE Range</Label>
                            <MinMaxInputs
                                min={estimatedCpe?.min}
                                max={estimatedCpe?.max}
                                debounce={500}
                                onChange={(min, max) => {
                                    onFiltersChange({ ...filters, estimatedCpe: minMax(min, max) });
                                }}
                            />
                        </div>
                    </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="collaboration">
                    <Accordion.Trigger className={styles.trigger}>
                        <span className={styles.triggerInner}>
                            <HeartHandshake className={styles.triggerIcon} />
                            <span>Collaboration</span>
                            {collaborationCount > 0 && <Badge size="sm">{collaborationCount}</Badge>}
                        </span>
                    </Accordion.Trigger>
                    <Accordion.Content className={styles.content}>
                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Response Rate (%)</Label>
                            <Input
                                type="number"
                                placeholder="Min %"
                                className={styles.numberInput}
                                min={0}
                                max={100}
                                value={responseRate}
                                debounce={500}
                                onChange={(e) => {
                                    onFiltersChange({ ...filters, responseRate: toNumberOrUndefined(e.target.value) });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Avg. Draft Time</Label>
                            <AverageDraftTimeFilter
                                value={avgDraftTime}
                                onChange={(option) => {
                                    onFiltersChange({ ...filters, avgDraftTime: option });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Last Booked or Added</Label>
                            <LastUsedFilter
                                value={lastUsed}
                                onChange={(value) => {
                                    onFiltersChange({ ...filters, lastUsed: value });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Posting Frequency</Label>
                            <PostingFrequencyFilter
                                value={postingFrequency}
                                onChange={(value) => {
                                    onFiltersChange({ ...filters, postingFrequency: value });
                                }}
                            />
                        </div>
                    </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="cost">
                    <Accordion.Trigger className={styles.trigger}>
                        <span className={styles.triggerInner}>
                            <DollarSign className={styles.triggerIcon} />
                            <span>Cost</span>
                            {costCount > 0 && <Badge size="sm">{costCount}</Badge>}
                        </span>
                    </Accordion.Trigger>
                    <Accordion.Content className={styles.content}>
                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Cost Range</Label>
                            <MinMaxInputs
                                min={costRange?.min}
                                max={costRange?.max}
                                debounce={500}
                                onChange={(min, max) => {
                                    onFiltersChange({ ...filters, costRange: minMax(min, max) });
                                }}
                            />
                        </div>

                        <div className={styles.hasCostRow}>
                            <Label className={styles.filterLabel}>Has Cost</Label>
                            <Switch
                                checked={Boolean(hasCost)}
                                onCheckedChange={(checked) => {
                                    onFiltersChange({ ...filters, hasCost: checked });
                                }}
                                size="lg"
                            />
                        </div>
                    </Accordion.Content>
                </Accordion.Item>

                <Accordion.Item value="audio">
                    <Accordion.Trigger className={styles.trigger}>
                        <span className={styles.triggerInner}>
                            <Music className={styles.triggerIcon} />
                            <span>Audio</span>
                            {audioCount > 0 && <Badge size="sm">{audioCount}</Badge>}
                        </span>
                    </Accordion.Trigger>
                    <Accordion.Content className={styles.content}>
                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>TikTok Audio URLs</Label>
                            <TiktokAudioUrlFilter
                                value={tiktokAudioIds ?? []}
                                onChange={(value) => onFiltersChange({ ...filters, tiktokAudioIds: value })}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Spotify Songs</Label>
                            <SpotifyTrackFilter
                                selectedSongSpotifyIds={spotifyTrackIds ?? []}
                                onChange={(spotifyTrackIds) => {
                                    onFiltersChange({ ...filters, spotifyTrackIds });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Spotify Artists</Label>
                            <SpotifyArtistFilter
                                value={spotifyArtistIds ?? []}
                                onChange={(next) => {
                                    onFiltersChange({
                                        ...filters,
                                        spotifyArtistIds: next.length ? next : undefined,
                                    });
                                }}
                            />
                        </div>

                        <div className={styles.filterItem}>
                            <Label className={styles.filterLabel}>Genres</Label>
                            <SpotifyGenreFilter
                                value={spotifyGenres ?? []}
                                onChange={(next) => {
                                    onFiltersChange({
                                        ...filters,
                                        spotifyGenres: next.length ? next : undefined,
                                    });
                                }}
                            />
                        </div>
                    </Accordion.Content>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}
